<template>
  <div class="work-times edit-item" v-if="editingData && editingData.work_times && options">
    <div class="head"><b>就業時間</b></div>
    <div class="data flex flex-align-center">
      <p v-if="mode===OptionConst.KOYOUKEIYAKU_EDIT_MODE.MODIFY && editingData.koyoukeiyaku_is_used">※既に打刻・申請が存在しているため、登録済みの雇用契約就業時間の削除できません。</p>
      <template v-if="editingData.work_times.length < 1">
        <p>無し</p>
        <div class="row-button" v-if="!disabled">
          <i class="fa-solid fa-circle-plus" @click="addWorkTime()"></i>
        </div>
      </template>
      <WorkTimesEditItem v-else v-for="(workTime, index) in editingData.work_times" :key="index" :workTime="workTime"
        :options="options" :index="index" :canAdd="max > editingData.work_times.length" :canRemove="canRemove(workTime, editingData.koyoukeiyaku_is_used)"
        @add="addWorkTime" @remove="removeWorkTime" :disabled="disabled" :mode="mode"></WorkTimesEditItem>
    </div>
  </div>
</template>

<script lang="ts">
import { KoyouKeiyakuOption, KoyouKeiyakuTime } from '@/types/KoyouKeiyaku';
import { KoyouKeiyakuDetail } from '@/types/KoyouKeiyakuDetail';
import { defineComponent, onMounted } from 'vue';
import { useTableRow } from "@/composable/common/tablerow";
import OptionConst from '@/const/Option';
import { DialogService } from '@/service/DialogService';
import WorkTimesEditItem from '@/components/koyoukeiyaku/parts/WorkTimesEditItem.vue';

export default defineComponent({
  props: {
    editingData: {
      type: Object as () => KoyouKeiyakuDetail | null,
      require: true,
      default: null
    },
    currentData: {
      type: Object as () => KoyouKeiyakuDetail | null,
      require: true,
      default: null
    },
    options: {
      type: Object as () => KoyouKeiyakuOption | null,
      require: true,
      default: null,
    },
    max: {
      type: Number,
      require: false,
      default: 4,
    },
    min: {
      type: Number,
      require: false,
      default: 1,
    },
    disabled: {
      type: Object as () => boolean,
      default: false
    },
    mode: {
      // モード 新規新規(create)／既存契約からの新規作成(from)／編集(edit)／承認後の編集（modify）の４モードを想定
      type: String,
      require: true,
      default: null
    }
  },
  setup(props) {
    const { addRowNoRef, removeTableRow } = useTableRow();

    onMounted(async () => {
      if (props.editingData?.work_times) {
        if (props.editingData.work_times.length > props.max) {
          // 最大個数に併せて切り捨て
          props.editingData.work_times.splice(props.max)
          await DialogService.warning({
            html: '就業時間の登録数が上限（' + props.max + '個）を超えたため切り捨てられました。<br>上限以上必要な場合、過去に上限以上登録できていた場合は社内で上限を相談してください。<br>切り捨て後に保存すると切り捨てられた就業時間情報は復元できないためご注意ください。'
          })
        }

        if (props.editingData.work_times.length < props.min) {
          // 最小個数に併せて初期値を設定
          for (let i = 0; i < props.min; i++) {
            props.editingData.work_times[i] = new KoyouKeiyakuTime()
          }
        }
      }
    })

    // 行追加
    const addWorkTime = () => {
      if (!props.editingData) {
        return
      }
      if (!props.editingData.work_times) {
        props.editingData.work_times = []
      }
      addRowNoRef(props.editingData.work_times, (new KoyouKeiyakuTime));
    }

    // 行追加
    const removeWorkTime = (index: number) => {
      if (!props.editingData) {
        return
      }
      if (!props.editingData.work_times) {
        props.editingData.work_times = []
      }
      removeTableRow(props.editingData.work_times, index);
    }

    const canRemove = (workTime: KoyouKeiyakuTime, KoyouKeiyakuIsUsed: boolean) => {
      //debugger
      if (props.disabled) {
        return false
      }

      if (!props.editingData) {
        return false
      }

      if (!props.editingData.work_times) {
        return false
      }

      if (props.min >= props.editingData.work_times.length) {
        return false
      }

      if(props.mode === OptionConst.KOYOUKEIYAKU_EDIT_MODE.MODIFY && workTime.koyou_keiyaku_time_id && KoyouKeiyakuIsUsed) {
        return false
      }

      return true
    }
    return {
      removeTableRow,
      addWorkTime,
      removeWorkTime,
      OptionConst,
      max: props.max,
      min: props.min,
      canRemove
    }
  },
  components: {
    WorkTimesEditItem
  }
})
</script>

<style lang="scss" scoped>
@import '@scss/components/koyoukeiyaku.scss';
@import '@scss/components/koyoukeiyaku-edit.scss';
@import '@scss/components/koyoukeiyaku-work_times.scss';
</style>
